import {useState, useRef} from 'react'
import PropTypes from 'prop-types'
import {Box} from '@mui/material'
import CategoryHeader from './CategoryHeader'
import FilterPopover from './FilterPopover'
import {StyledCategoryDivider} from './FilterTabsAndLayoutStyles'
/**
 * FilterCategorySection Component
 *
 * Static section for a filter category.
 * Used in the "All" tab to show all categories as always-visible sections.
 *
 * @param {Object} props
 * @param {Object} props.category - Category data from config
 * @param {string} props.category.title - Category title
 * @param {string} props.category.desc - Category description
 * @param {string} props.category.id - Category ID
 * @param {Array} props.category.filters - Array of filters in this category
 * @param {React.ReactNode} props.children - Filter components to render
 * @param {number} props.selectedCount - Number of selected filters in this category
 */
const FilterCategorySection = ({category, children, selectedCount = 0, tabsContainerRef}) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const buttonRef = useRef(null)

  const handleAddClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)

  const filters = category.filters || []

  // Use customName if available (custom name from backend), fallback to title
  const categoryTitle = category.customName || category.title
  // Keep original title for analytics (without customization)
  const originalCategoryTitle = category.title

  return (
    <Box p={1} pb={0}>
      <CategoryHeader
        title={categoryTitle}
        selectedCount={selectedCount}
        onAddClick={handleAddClick}
        buttonRef={buttonRef}
        isOpen={open}
      />

      {open && (
        <FilterPopover
          open={open}
          anchorEl={tabsContainerRef?.current}
          onClose={handleClose}
          categoryTitle={categoryTitle}
          originalCategoryTitle={originalCategoryTitle}
          categoryDesc={category.desc}
          footerMessage={category.footerMessage}
          filters={filters}
          categoryId={category.id}
        />
      )}

      <Box>{children}</Box>
      <StyledCategoryDivider />
    </Box>
  )
}

FilterCategorySection.propTypes = {
  category: PropTypes.shape({
    title: PropTypes.string.isRequired,
    desc: PropTypes.string,
    id: PropTypes.string.isRequired,
    filters: PropTypes.array,
    footerMessage: PropTypes.string,
    customName: PropTypes.string
  }).isRequired,
  children: PropTypes.node,
  selectedCount: PropTypes.number,
  tabsContainerRef: PropTypes.object // Ref to TabsContainer for popover anchoring
}

export default FilterCategorySection
